<script lang="ts">
export const buttonCls = cva("btn focus:ring-neutral truncate", {
	variants: {
		color: {
			default: null,
			neutral: "btn-neutral focus:ring-neutral",
			primary: "btn-primary focus:ring-primary",
			secondary: "btn-secondary focus:ring-secondary",
			accent: "btn-accent focus:ring-accent",
			info: "btn-info focus:ring-info",
			success: "btn-success focus:ring-success",
			warning: "btn-warning focus:ring-warning",
			error: "btn-error focus:ring-error",
		},
		variant: {
			default: null,
			link: "btn-link",
			outline:
				"btn-outline disabled:border-border border-border bg-base-100 hover:bg-focus focus-visible:bg-focus hover:text-[var(--btn-color)] focus-visible:text-[var(--btn-color)] disabled:border",
			soft: "btn-soft",
			ghost: "btn-ghost disabled:text-base-content/20 focus-visible:ring-0",
			dash: "btn-dash",
		},
		shape: {
			square: "btn-square",
			round: "btn-circle",
		},
		size: {
			xs: "btn-xs [&_svg]:data-[custom-size=false]:icon-sm",
			sm: "btn-sm [&_svg]:data-[custom-size=false]:icon-sm",
			md: "btn-md [&_svg]:data-[custom-size=false]:icon-md",
			lg: "btn-lg [&_svg]:data-[custom-size=false]:icon-lg",
		},
		showFocus: {
			true: "focus:not-active:outline-2 focus:not-active:outline-solid focus:not-active:isolate",
			false: null,
		},
	},
	defaultVariants: {
		size: "sm",
		variant: "outline",
		color: "default",
	},
	compoundVariants: [
		{
			variant: "ghost",
			color: "primary",
			class: "text-primary hover:text-primary-content focus-visible:text-primary-content",
		},
		{
			variant: "ghost",
			color: "secondary",
			class: "text-secondary hover:text-secondary-content focus-visible:text-secondary-content",
		},
		{
			variant: "ghost",
			color: "accent",
			class: "text-accent hover:text-accent-content focus-visible:text-accent-content",
		},
		{
			variant: "ghost",
			color: "success",
			class: "text-success hover:text-success-content focus-visible:text-success-content",
		},
		{
			variant: "ghost",
			color: "warning",
			class: "text-warning hover:text-warning-content focus-visible:text-warning-content",
		},
		{
			variant: "ghost",
			color: "error",
			class: "text-error hover:text-error-content focus-visible:text-error-content",
		},
		{
			variant: "soft",
			color: "neutral",
			class: "dark:text-base-content",
		},
	],
});

type Variant = VariantProps<typeof buttonCls>;

type DeprecatedShapeProps = {
	/**
	 * @deprecated
	 */
	round?: boolean;

	/**
	 * @deprecated
	 */
	square?: boolean;
};

type DeprecatedVariantProps = {
	/**
	 * @deprecated
	 */
	outlined?: boolean;

	/**
	 * @deprecated
	 */
	ghost?: boolean;
};

export interface ButtonProps
	extends BasePrimitiveProps,
		DeprecatedSizeProps,
		DeprecatedShapeProps,
		DeprecatedVariantProps {
	loading?: boolean;
	color?: Variant["color"];
	variant?: Variant["variant"];
	shape?: Variant["shape"];
	size?: Variant["size"];
	showFocus?: boolean;
}

interface Props
	extends ButtonProps,
		/* @vue-ignore */ Omit<ButtonHTMLAttributes, keyof ButtonProps> {}
</script>

<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import { Primitive, useForwardExpose } from "reka-ui";
import { computed, useAttrs, warn, type ButtonHTMLAttributes } from "vue";
import type { BasePrimitiveProps } from "../../lib/base-props";
import { convertDeprecatedSize, type DeprecatedSizeProps } from "../../lib/size";
import { cn, htmlAttrs } from "../../lib/utils";

defineOptions({
	inheritAttrs: false,
});

const { as = "button", ...props } = defineProps<Props>() as ButtonProps;

const attrs = useAttrs() as Omit<ButtonHTMLAttributes, keyof ButtonProps>;

const size = computed(() => {
	if (props.size != null) {
		return props.size;
	}

	return convertDeprecatedSize(props);
});

const variant = computed<Props["variant"]>(() => {
	if (props.variant != null) {
		return props.variant;
	}

	if (props.outlined) {
		return "outline";
	}

	if (props.ghost) {
		return "ghost";
	}

	return null;
});

const shape = computed<Props["shape"] | null>(() => {
	if (props.shape != null) {
		return props.shape;
	}

	if (props.round) {
		return "round";
	}

	if (props.square) {
		return "square";
	}
});

if (__DEV__) {
	if (props.asChild && props.loading) {
		warn("[UI] Button `asChild` props is not compatible with the `loading` props.");
	}
}

const { forwardRef } = useForwardExpose();
</script>

<template>
	<Primitive
		:ref="forwardRef"
		:as="as"
		:asChild="props.asChild"
		:class="cn(buttonCls({ ...props, variant, size, shape }))"
		type="button"
		v-bind="htmlAttrs(attrs)"
	>
		<span v-if="loading && !props.asChild" class="loading loading-spinner"></span>
		<slot v-else />
	</Primitive>
</template>
